// Custom donut chart matching the factsheet screenshot style
// Layout: legend section (left, flex) + donut chart section (right, fixed width)
// Legend shows label | percentage | colored square for each data item
import { FC } from 'react';
import { Box } from '@mui/material';
import ReactApexChart from 'react-apexcharts';

interface IChartDataItem {
  label: string;
  value: number;
  color?: string;
}

interface CustomDonutChartProps {
  /** Chart data items (label + value + optional color) */
  data: IChartDataItem[];
  /** ApexChart ID used for PDF image capture via ApexCharts.exec() */
  id?: string;
  /** Fixed width of the chart (donut) section in px (defaults to 160) */
  chartWidth?: number;
  /** Donut chart height in px (defaults to chartWidth value) */
  height?: number;
  /** Donut hole size as percentage string (defaults to '60%') */
  donutSize?: string;
  /** Optional color palette override. Falls back to item.color then DONUT_COLORS */
  colors?: string[];
  /** White gaps between segments (matches pie legend / PVM TOP-10 row splits) */
  showSegmentGaps?: boolean;
  /** Tighter legend rows (PVM page 2) */
  compact?: boolean;
}

/** Default color palette matching the factsheet screenshot design */
const DONUT_COLORS = ['#003f7a', '#1a6cb4', '#4a9fd9', '#58585a', '#a7a8ab'];

/** Format number as German-style percentage (comma as decimal separator) */
const formatPercent = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);
  return num.toFixed(2).replace('.', ',') + '%';
};

/** Get color for chart item, falling back to the default palette */
const getItemColor = (item: IChartDataItem, index: number, palette?: string[]): string => {
  const colors = palette && palette.length > 0 ? palette : DONUT_COLORS;
  return item.color || colors[index % colors.length];
};

export const CustomDonutChart: FC<CustomDonutChartProps> = ({
  data,
  id,
  chartWidth = 160,
  height,
  donutSize = '60%',
  colors: colorsProp,
  showSegmentGaps = false,
  compact = false,
}) => {
  const chartHeight = height || chartWidth;
  const rowHeight = compact ? 15 : 20;
  const rowMb = showSegmentGaps ? (compact ? '2px' : '3.5px') : '8px';
  const fontSize = compact ? 11 : 12;
  const textColor = compact ? '#26364A' : '#000';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {/* Legend section - takes remaining space */}
      <Box sx={{ flex: 1, pr: compact ? '8px' : '12px' }}>
        {data.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#D2D2D4',
              mb: rowMb,
              px: '4px',
              height: rowHeight,
            }}
          >
            <Box sx={{ flex: 1, fontSize, color: textColor, pl: 1 }}>
              {item.label}
            </Box>
            <Box
              sx={{
                fontSize,
                color: textColor,
                textAlign: 'right',
                mr: 1,
                minWidth: compact ? 52 : 60,
              }}
            >
              {formatPercent(item.value)}
            </Box>
            <Box
              sx={{
                width: compact ? 8 : 10,
                height: compact ? 8 : 10,
                backgroundColor: getItemColor(item, index, colorsProp),
                flexShrink: 0,
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Chart (donut) section - fixed width */}
      <Box sx={{ width: chartWidth, flexShrink: 0 }}>
        <ReactApexChart
          options={{
            chart: { id: id || undefined },
            labels: data.map((d) => d.label),
            colors: data.map((d, i) => getItemColor(d, i, colorsProp)),
            legend: { show: false },
            dataLabels: { enabled: false },
            stroke: showSegmentGaps
              ? { show: true, width: 2, colors: ['#ffffff'] }
              : { show: false },
            tooltip: { enabled: false },
            plotOptions: {
              pie: {
                donut: {
                  size: donutSize,
                },
              },
            },
          }}
          series={data.map((d) => +d.value)}
          type="donut"
          height={chartHeight}
        />
      </Box>
    </Box>
  );
};
